import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { formatTime } from '../../pricing/model/format'
import {
  EMPTY_VIEW,
  type OneCStructure,
  type StructureDetailsVm,
  type StructureFactVm,
  type StructureIntentState,
  type StructureLink,
  type StructureNodeKind,
  type StructureNodeVm,
  type StructureScreenVm,
  type StructureUnit,
  type StructureViewState,
  type StructureWarehouse,
  type WarehouseOption,
  type WarehouseOptionGroup,
  type WarehousePickVm,
} from './types'

/**
 * Селекторы структуры 1С: (ответ службы + намерение) → дерево экрана и общий
 * выбор склада. RootState пакет не знает: binding подаёт результат
 * `onecStructure.select(args)` и свой срез `onec.structure`.
 */
export type StructureResult = QueryResultLike<OneCEnvelope<OneCStructure>> & {
  /** Когда запрос получил последний ответ (поле результата RTK Query): им отказ «Обновить» сверяется с чтением после него. */
  fulfilledTimeStamp?: number
}

const NO_ORGANIZATION = 'group:no-organization'
const EMPTY_WAREHOUSES: readonly StructureWarehouse[] = []
const EMPTY_OPTIONS: readonly WarehouseOption[] = []
const EMPTY_GROUPS: readonly WarehouseOptionGroup[] = []

/** Подпись склада в выпадающих списках — та же, что была у справочника складов поставки. */
export const warehouseLabel = (warehouse: Pick<StructureWarehouse, 'name' | 'organizationName'>): string =>
  warehouse.organizationName === null ? warehouse.name : `${warehouse.name} · ${warehouse.organizationName}`

// --- склады базы --------------------------------------------------------------------------------

const warehousesCache = new WeakMap<OneCStructure, readonly StructureWarehouse[]>()

/** Склады базы (только вид «склад») с именами организации и подразделения. Мемоизировано по ответу службы. */
export function selectStructureWarehouses(structure: OneCStructure): readonly StructureWarehouse[] {
  const cached = warehousesCache.get(structure)
  if (cached !== undefined) return cached
  const organizations = new Map(structure.organizations.map((item) => [item.key, item.name]))
  const units = new Map(structure.units.map((item) => [item.key, item.name]))
  const linked = (link: StructureLink, names: Map<string, string>) =>
    link.state === 'linked' && link.key !== null ? (names.get(link.key) ?? null) : null
  const result = structure.units
    .filter((unit) => unit.kind === 'warehouse')
    .map((unit) => ({
      refKey: unit.key,
      name: unit.name,
      organizationName: linked(unit.organization, organizations),
      departmentName: linked(unit.department, units),
      hasRetailPriceType: unit.hasRetailPriceType,
    }))
  warehousesCache.set(structure, result)
  return result
}

const pickCache = new WeakMap<OneCEnvelope<OneCStructure>, Omit<WarehousePickVm, 'query'>>()

function pickOf(envelope: OneCEnvelope<OneCStructure>): Omit<WarehousePickVm, 'query'> {
  const cached = pickCache.get(envelope)
  if (cached !== undefined) return cached
  const structure = envelope.data
  const warehouses = selectStructureWarehouses(structure)
  const options: WarehouseOption[] = []
  const grouped = new Map<string, WarehouseOption[]>()
  for (const warehouse of warehouses) {
    const option = { value: warehouse.refKey, label: warehouseLabel(warehouse) }
    options.push(option)
    const group = `${warehouse.organizationName ?? 'Без организации'} · ${warehouse.departmentName ?? 'без подразделения'}`
    const bucket = grouped.get(group) ?? []
    bucket.push(option)
    grouped.set(group, bucket)
  }
  const result = {
    warehouses,
    options,
    groups: [...grouped.entries()].map(([label, items]) => ({ label, options: items })),
    ready: warehouses.length > 0,
    configurationVersion: envelope.configurationVersion,
  }
  pickCache.set(envelope, result)
  return result
}

/**
 * Общий выбор склада базы для поставок, инвентаризации и ценников: один
 * запрос структуры, одна подпись. Склады — только из ответа 1С (RP-LSB2): при
 * отказе прежний список из кэша запроса не показывается — 1С не ответила,
 * значит списка нет, есть её текст и «Повторить». Пустой список складов —
 * состояние `empty`, а не готовый пустой выбор.
 */
export function warehousePickOf(result: StructureResult): WarehousePickVm {
  const query = queryView(result, (envelope) => selectStructureWarehouses(envelope.data).length === 0)
  if (result.data === undefined || query.status !== 'ready') {
    return { query, warehouses: EMPTY_WAREHOUSES, options: EMPTY_OPTIONS, groups: EMPTY_GROUPS, ready: false, configurationVersion: '' }
  }
  return { query, ...pickOf(result.data) }
}

// --- экран «Структура 1С» ------------------------------------------------------------------------

interface TreeNode {
  id: string
  kind: StructureNodeKind
  name: string
  caption: string | null
  children: TreeNode[]
}

const KIND_CAPTIONS: Readonly<Record<StructureUnit['kind'], string>> = {
  department: 'Подразделение',
  warehouse: 'Склад',
  other: 'Прочая структурная единица',
}

function unitNode(unit: StructureUnit): TreeNode {
  const kind: StructureNodeKind = unit.kind
  const caption = unit.nativeType !== null && unit.nativeType !== KIND_CAPTIONS[unit.kind] ? `${KIND_CAPTIONS[unit.kind]} (${unit.nativeType})` : KIND_CAPTIONS[unit.kind]
  return { id: unit.key, kind, name: unit.name, caption, children: [] }
}

/**
 * Дерево: организация → подразделение → склад. Единица встаёт под своё
 * подразделение (ближайшее вверх по иерархии), иначе — под свою организацию,
 * иначе — в группу «Без организации». У подразделения «подразделение» —
 * родительское, поэтому вложенные подразделения встают друг под друга.
 */
function buildTree(structure: OneCStructure): TreeNode[] {
  const organizations = new Map<string, TreeNode>(
    structure.organizations.map((item) => [item.key, { id: item.key, kind: 'organization', name: item.name, caption: 'Организация', children: [] }]),
  )
  const units = new Map(structure.units.map((unit) => [unit.key, unitNode(unit)]))
  const loose: TreeNode = { id: NO_ORGANIZATION, kind: 'group', name: 'Без организации', caption: 'организация не задана или не ведётся', children: [] }
  for (const unit of structure.units) {
    const node = units.get(unit.key)
    if (node === undefined) continue
    const department = unit.department.state === 'linked' && unit.department.key !== null ? units.get(unit.department.key) : undefined
    const organization =
      unit.organization.state === 'linked' && unit.organization.key !== null ? organizations.get(unit.organization.key) : undefined
    ;(department ?? organization ?? loose).children.push(node)
  }
  const rank: Readonly<Record<StructureNodeKind, number>> = { organization: 0, group: 0, department: 1, warehouse: 2, other: 3 }
  const sort = (nodes: TreeNode[]) => {
    nodes.sort((left, right) => rank[left.kind] - rank[right.kind] || left.name.localeCompare(right.name, 'ru'))
    nodes.forEach((node) => sort(node.children))
  }
  const roots = [...organizations.values(), ...(loose.children.length > 0 ? [loose] : [])]
  roots.forEach((root) => sort(root.children))
  return roots
}

function countWarehouses(node: TreeNode): number {
  return node.children.reduce((sum, child) => sum + (child.kind === 'warehouse' ? 1 : 0) + countWarehouses(child), 0)
}

function flatten(roots: readonly TreeNode[], view: StructureViewState): StructureNodeVm[] {
  const search = view.search.trim().toLocaleLowerCase('ru')
  const matches = (node: TreeNode): boolean =>
    search === '' || node.name.toLocaleLowerCase('ru').includes(search) || node.children.some(matches)
  const result: StructureNodeVm[] = []
  const walk = (node: TreeNode, depth: number) => {
    if (!matches(node)) return
    // Поиск раскрывает путь к найденному, иначе совпадение пряталось бы в свёрнутом узле.
    const expanded = search !== '' || !view.collapsed.includes(node.id)
    result.push({
      id: node.id,
      kind: node.kind,
      name: node.name,
      caption: node.caption,
      depth,
      expandable: node.children.length > 0,
      expanded,
      selected: view.selected === node.id,
      warehouses: countWarehouses(node),
    })
    if (expanded) node.children.forEach((child) => walk(child, depth + 1))
  }
  roots.forEach((root) => walk(root, 0))
  return result
}

/** Связь словами: «не задано» и «в конфигурации нет» — разные ответы. */
function linkFact(label: string, link: StructureLink, names: Map<string, string>): StructureFactVm {
  switch (link.state) {
    case 'linked':
      return { label, value: (link.key !== null ? names.get(link.key) : undefined) ?? link.key ?? '—', tone: 'ok' }
    case 'empty':
      return { label, value: 'не задано в 1С', tone: 'missing' }
    case 'unresolved':
      return { label, value: 'ссылка на элемент, которого нет среди действующих (помечен на удаление?)', tone: 'missing' }
    default:
      return { label, value: 'в конфигурации этой базы такой связи нет', tone: 'unsupported' }
  }
}

function details(structure: OneCStructure, roots: readonly TreeNode[], selected: string | null): StructureDetailsVm | null {
  if (selected === null) return null
  const organization = structure.organizations.find((item) => item.key === selected)
  if (organization !== undefined) {
    const root = roots.find((node) => node.id === organization.key)
    return {
      id: organization.key,
      kind: 'organization',
      name: organization.name,
      facts: [
        { label: 'Код', value: organization.code ?? '—', tone: 'ok' },
        { label: 'Складов', value: String(root === undefined ? 0 : countWarehouses(root)), tone: 'ok' },
      ],
    }
  }
  const unit = structure.units.find((item) => item.key === selected)
  if (unit === undefined) return null
  const organizations = new Map(structure.organizations.map((item) => [item.key, item.name]))
  const units = new Map(structure.units.map((item) => [item.key, item.name]))
  return {
    id: unit.key,
    kind: unit.kind,
    name: unit.name,
    facts: [
      { label: 'Вид', value: KIND_CAPTIONS[unit.kind], tone: 'ok' },
      { label: 'Тип в 1С', value: unit.nativeType ?? 'не задан', tone: unit.nativeType === null ? 'missing' : 'ok' },
      { label: 'Код', value: unit.code ?? '—', tone: 'ok' },
      linkFact('Организация', unit.organization, organizations),
      linkFact(unit.kind === 'department' ? 'Входит в подразделение' : 'Подразделение', unit.department, units),
      linkFact('Родитель в иерархии', unit.parent, units),
      ...(unit.kind === 'warehouse'
        ? [{ label: 'Розничный вид цен', value: unit.hasRetailPriceType ? 'задан' : 'не задан', tone: unit.hasRetailPriceType ? 'ok' : 'missing' } as const]
        : []),
    ],
  }
}

const plural = (count: number, one: string, few: string, many: string) => {
  const tens = count % 100
  const units = count % 10
  const word = tens >= 11 && tens <= 14 ? many : units === 1 ? one : units >= 2 && units <= 4 ? few : many
  return `${count} ${word}`
}

function limitsOf(structure: OneCStructure): string[] {
  const limits: string[] = []
  if (!structure.relations.unitOrganization)
    limits.push('У складов этой конфигурации нет реквизита организации: связь «склад → организация» в 1С не ведётся.')
  if (!structure.relations.departments)
    limits.push('Подразделения в этой конфигурации не отличаются от складов: у структурной единицы нет типа.')
  else if (!structure.relations.unitDepartment)
    limits.push('Склады не связаны с подразделениями: у справочника структурных единиц нет иерархии.')
  return limits
}

export interface StructureScreenInput {
  resourceId: string
  structure: StructureResult
  intent: StructureIntentState
  /**
   * Состояние «Обновить из 1С»: её отказ — тоже отказ 1С, а не заметка над прежней структурой.
   * `startedTimeStamp` — когда нажали: чтение, ответившее позже (окно открыли снова), отказ снимает.
   */
  refresh: { isLoading: boolean; error?: unknown; startedTimeStamp?: number }
}

/**
 * Вид экрана: отказ «Обновить из 1С» показывается так же, как отказ чтения —
 * текстом 1С и «Повторить», без структуры, прочитанной раньше (принцип
 * владельца: при отказе — понятная ошибка, а не прежний список).
 */
function screenQuery(
  result: StructureResult,
  refresh: StructureScreenInput['refresh'],
): { query: QueryView; retry: 'read' | 'refresh' } {
  const answeredLater =
    refresh.startedTimeStamp !== undefined && result.fulfilledTimeStamp !== undefined && result.fulfilledTimeStamp > refresh.startedTimeStamp
  const refreshError = refresh.error
  if (!refresh.isLoading && refreshError !== undefined && !answeredLater) {
    return {
      query: queryView({ error: refreshError as StructureResult['error'], isUninitialized: false, isLoading: false, isError: true, isSuccess: false }, () => false),
      retry: 'refresh',
    }
  }
  return { query: queryView(result, (envelope) => envelope.data.organizations.length === 0 && envelope.data.units.length === 0), retry: 'read' }
}

export const makeSelectStructureScreenVm = () => {
  const selectTree = createSelector([(structure: OneCStructure) => structure], buildTree)
  return createSelector(
    [
      (input: StructureScreenInput) => input.resourceId,
      (input: StructureScreenInput) => input.structure,
      (input: StructureScreenInput) => input.intent[normalizeRid(input.resourceId)] ?? EMPTY_VIEW,
      (input: StructureScreenInput) => input.refresh.isLoading,
      (input: StructureScreenInput) => input.refresh.error,
      (input: StructureScreenInput) => input.refresh.startedTimeStamp,
    ],
    (resourceId, result, view, refreshing, refreshError, refreshStarted): StructureScreenVm => {
      const { query, retry } = screenQuery(result, { isLoading: refreshing, error: refreshError, startedTimeStamp: refreshStarted })
      // Структура — только при ответе 1С: при отказе чтения или «Обновить» прежней в окне нет.
      const structure = query.status === 'ready' || query.status === 'empty' ? result.data?.data : undefined
      const roots = structure === undefined ? [] : selectTree(structure)
      return {
        resourceId,
        query,
        search: view.search,
        nodes: flatten(roots, view),
        details: structure === undefined ? null : details(structure, roots, view.selected),
        summary:
          structure === undefined
            ? null
            : [
                plural(structure.organizations.length, 'организация', 'организации', 'организаций'),
                plural(structure.units.filter((unit) => unit.kind === 'department').length, 'подразделение', 'подразделения', 'подразделений'),
                plural(structure.units.filter((unit) => unit.kind === 'warehouse').length, 'склад', 'склада', 'складов'),
              ].join(', '),
        readAt: structure === undefined ? null : `Прочитано из 1С в ${formatTime(structure.observedAt)}`,
        limits: structure === undefined ? [] : limitsOf(structure),
        refreshing,
        retry,
      }
    },
  )
}
